import { Tooltip } from '@design-system/components/tooltip/tooltip';

import {
  isReady,
  metCount,
  totalCount,
  unmetCount,
} from '../../model/programs.readiness';
import { ProgramReadiness } from '../../model/programs.types';

import { ReadinessIndicator } from './ReadinessIndicator';
import * as styles from './ProgramReadinessHeaderPill.css';

type ProgramReadinessHeaderPillProps = {
  readiness: ProgramReadiness;
  onClick: (anchor: HTMLElement) => void;
};

const toLabel = (readiness: ProgramReadiness): string =>
  isReady(readiness) ? 'Ready' : `${metCount(readiness)}/${totalCount()} Ready`;

const toTooltip = (readiness: ProgramReadiness): string =>
  isReady(readiness)
    ? 'All publication criteria met — click for details'
    : `${unmetCount(
        readiness,
      )} publication criteria missing — click for details`;

/**
 * Floating pill placed in the Program detail header next to the Save button.
 * White card with a soft shadow + light border, a progress ring (partial) or
 * solid green check (all met) on the left, and an "X/4 Ready" / "Ready"
 * label on the right. The label switches to green when all criteria are met
 * so the ready state reads at a glance.
 *
 * The pill is the popover anchor — `onClick` receives the current button so
 * `ProgramReadinessPopover` can position itself directly under the badge.
 * The popover itself is reused unchanged from the Programs list page; this
 * component only owns the trigger surface.
 */
export const ProgramReadinessHeaderPill = ({
  readiness,
  onClick,
}: ProgramReadinessHeaderPillProps) => {
  const ready = isReady(readiness);

  return (
    <Tooltip
      arrow
      variant="dark"
      title={toTooltip(readiness)}
      placement="top"
      tooltipProps={{ maxWidth: 240 }}
    >
      <button
        type="button"
        className={styles.pill}
        onClick={(event) => onClick(event.currentTarget)}
        aria-label={toTooltip(readiness)}
      >
        <ReadinessIndicator readiness={readiness} size={18} />
        <span className={styles.label({ ready })}>{toLabel(readiness)}</span>
      </button>
    </Tooltip>
  );
};
